import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import {
  AddressInput,
  Button,
  FieldWrapper,
  Input,
  InputErrorMessage,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@commenda/kakashi';
import { DatePicker } from '@commenda/kakashi/src/components/DatePicker';
import { defaultAddressSchema } from '@commenda/schema/address';
import { IAddress } from '@commenda/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AddressType,
  CorporationType,
  Country,
  IncorporationRequestStatus,
} from '@prisma/client';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import Required from '../FormElements/Required';
import {
  CompanyNameInput,
  FormInputWrapper,
  IncorporationFormHeading,
} from '../common';
import {
  CompanyBuySellStatus,
  PrincipalPlaceOfBusinessOptions,
  UKLocationOptions,
  UKMailingAddressOptions,
  YesNoOptions,
} from './Common';
import { CompanyInfoFormFields, companyInfoSchema } from './schema';

const determineDateSelection = (data: WorkRequestResponseType) => {
  if (data?.requestData?.hasNoSuchStartSellDate)
    return CompanyBuySellStatus.NoSuchDate;
  if (data?.requestData?.startDateSameAsSetUp)
    return CompanyBuySellStatus.SameDate;
  if (data?.requestData?.hasDifferentDateInTheFuture)
    return CompanyBuySellStatus.DifferentDate;
  return null;
};

const setAddressFromFromData = (
  formData: CompanyInfoFormFields,
  operatingAddress: IAddress,
  mailingAddress: IAddress,
  addressTypesToDelete: AddressType[]
) => {
  const addresses: IAddress[] = [];

  // Previously used provided mailing address. But now opting for commenda to provide UK address, so remove the mailing address
  if (formData.hasOptedForUKMailingAddress && mailingAddress?.id) {
    if (!addressTypesToDelete.includes(AddressType.MailingAddress)) {
      addressTypesToDelete.push(AddressType.MailingAddress);
    }
  }

  if (!formData.hasOptedForUKMailingAddress) {
    addresses.push({
      ...(mailingAddress ? { id: mailingAddress.id } : {}),
      type: AddressType.MailingAddress,
      addressLine1: formData?.mailingAddress?.addressLine1,
      addressLine2: formData?.mailingAddress?.addressLine2,
      city: formData?.mailingAddress?.city,
      country: formData?.mailingAddress?.country,
      state: formData?.mailingAddress?.state,
      postalCode: formData?.mailingAddress?.postalCode,
    });
  }

  // Handle different principal place of business scenarios
  if (
    formData.principalPlaceOfBusiness ===
      PrincipalPlaceOfBusinessOptions.NoAddress &&
    operatingAddress?.id
  ) {
    if (!addressTypesToDelete.includes(AddressType.OperatingAddress))
      addressTypesToDelete.push(AddressType.OperatingAddress);
  }

  if (!formData.hasOptedForUKMailingAddress) {
    if (
      formData.principalPlaceOfBusiness ===
      PrincipalPlaceOfBusinessOptions.DifferentAddress
    ) {
      addresses.push({
        ...(operatingAddress ? { id: operatingAddress.id } : {}),
        type: AddressType.OperatingAddress,
        addressLine1: formData?.operatingAddress?.addressLine1,
        addressLine2: formData?.operatingAddress?.addressLine2,
        city: formData?.operatingAddress?.city,
        country: formData?.operatingAddress?.country,
        state: formData?.operatingAddress?.state,
        postalCode: formData?.operatingAddress?.postalCode,
      });
    } else if (
      formData.principalPlaceOfBusiness ===
      PrincipalPlaceOfBusinessOptions.SameAddress
    ) {
      // Use mailing address as operating address
      addresses.push({
        ...(operatingAddress ? { id: operatingAddress.id } : {}),
        type: AddressType.OperatingAddress,
        addressLine1: formData?.mailingAddress?.addressLine1,
        addressLine2: formData?.mailingAddress?.addressLine2,
        city: formData?.mailingAddress?.city,
        country: formData?.mailingAddress?.country,
        state: formData?.mailingAddress?.state,
        postalCode: formData?.mailingAddress?.postalCode,
      });
    }
  }

  return { addresses, addressTypesToDelete };
};

const CompanyInfo = ({
  next,
  data,
  mutate,
  token,
  setNotification,
}: {
  next: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  token: string;
  setNotification: TSetNotification;
}) => {
  const router = useRouter();

  const { id } = router.query;

  const mailingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.MailingAddress
  )?.value;

  const operatingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.OperatingAddress
  )?.value;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    clearErrors,
    setValue,
  } = useForm<CompanyInfoFormFields>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      companyLegalName: data?.requestData?.companyLegalName || '',
      companyLegalName2: data?.requestData?.companyLegalName2 || '',
      companyLegalName3: data?.requestData?.companyLegalName3 || '',
      // We won't populate suffix here, since the text input is just the whole corporation name,
      // and we aren't displaying "LTD" at the end. Customers may or may not type in "LTD" in the corporation name input.
      // It's better to be missing the corporation name suffix than to duplicate it.
      // companyNameSuffix: CorporationType.LTD,
      companyRegisteredEmailAddress:
        data?.requestData?.companyRegisteredEmailAddress || '',
      businessDescription: data?.requestData?.businessDescription || '',
      isConvertingToLimitedCompany:
        data?.requestData?.isConvertingToLimitedCompany ?? null,
      convertingToLimitedCompanyName:
        data?.requestData?.convertingToLimitedCompanyName || '',
      hasCT16Payments: data?.requestData?.hasCT16Payments ?? null,
      startDateSameAsSetUp: data?.requestData?.startDateSameAsSetUp ?? null,
      hasNoSuchStartSellDate: data?.requestData?.hasNoSuchDate ?? null,
      hasDifferentDateInTheFuture:
        data?.requestData?.hasDifferentDateInTheFuture ?? null,
      companyStartBuySellDate:
        data?.requestData?.companyStartBuySellDate ?? null,
      registrationLocation: data?.requestData?.registrationLocation || null,
      hasOptedForUKMailingAddress:
        data?.requestData?.hasOptedForUKMailingAddress ?? null,
      mailingAddress: mailingAddress ?? undefined,
      principalPlaceOfBusiness:
        data?.requestData?.hasPrincipalPlaceOfBusiness === true
          ? // Check if operating address is same as mailing address
            operatingAddress?.addressLine1 === mailingAddress?.addressLine1 &&
            operatingAddress?.addressLine2 === mailingAddress?.addressLine2 &&
            operatingAddress?.city === mailingAddress?.city &&
            operatingAddress?.state === mailingAddress?.state &&
            operatingAddress?.postalCode === mailingAddress?.postalCode &&
            operatingAddress?.country === mailingAddress?.country
            ? PrincipalPlaceOfBusinessOptions.SameAddress
            : PrincipalPlaceOfBusinessOptions.DifferentAddress
          : data?.requestData?.hasPrincipalPlaceOfBusiness === false
            ? PrincipalPlaceOfBusinessOptions.NoAddress
            : null,
      operatingAddress: operatingAddress ?? undefined,
    },
  });

  const [dateSelection, setDateSelection] = useState<string>(
    determineDateSelection(data)
  );

  const [addressesToDelete, setAddressToDelete] = useState<AddressType[]>([]);

  const onSubmit = async (formData: CompanyInfoFormFields) => {
    const { addresses, addressTypesToDelete } = setAddressFromFromData(
      formData,
      operatingAddress,
      mailingAddress,
      addressesToDelete
    );

    const payload = {
      requestData: {
        corporationType: CorporationType.PvtLtd,
        companyLegalName: formData.companyLegalName,
        companyLegalName2: formData.companyLegalName2,
        companyLegalName3: formData.companyLegalName3,
        // We won't populate suffix here, since the text input is just the whole corporation name,
        // and we aren't displaying "LTD" at the end. Customers may or may not type in "LTD" in the corporation name input.
        // It's better to be missing the corporation name suffix than to duplicate it.
        // companyNameSuffix: formData.companyNameSuffix || CorporationType.LTD,
        businessDescription: formData.businessDescription,
        companyRegisteredEmailAddress: formData.companyRegisteredEmailAddress,
        hasOptedForUKMailingAddress: formData?.hasOptedForUKMailingAddress,
        convertingToLimitedCompanyName:
          formData?.convertingToLimitedCompanyName || '',
        hasCT16Payments: formData.hasCT16Payments,
        hasDifferentDateInTheFuture:
          dateSelection === CompanyBuySellStatus.DifferentDate,
        companyStartBuySellDate:
          dateSelection === CompanyBuySellStatus.DifferentDate
            ? formData.companyStartBuySellDate
            : '',
        hasNoSuchStartSellDate:
          dateSelection === CompanyBuySellStatus.NoSuchDate,
        startDateSameAsSetUp: dateSelection === CompanyBuySellStatus.SameDate,
        registrationLocation: formData.hasOptedForUKMailingAddress
          ? ''
          : formData.registrationLocation,
        isConvertingToLimitedCompany: formData.isConvertingToLimitedCompany,
        hasPrincipalPlaceOfBusiness:
          formData.principalPlaceOfBusiness ===
            PrincipalPlaceOfBusinessOptions.DifferentAddress ||
          formData.principalPlaceOfBusiness ===
            PrincipalPlaceOfBusinessOptions.SameAddress,
      },
      addresses,
      ...(addressTypesToDelete.length > 0 ? { addressTypesToDelete } : []),
      status: IncorporationRequestStatus.InProgress,
    };

    const res = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: payload,
      },
      token
    );

    if (!res.ok) {
      setNotification({
        message:
          'Something went wrong, please try again later or contact Team Commenda if you need help',
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }

    setAddressToDelete([]);

    await mutate();

    next();
  };

  const isOptingInUKMailingAddress = (isOptingIn: boolean): void => {
    clearErrors('hasOptedForUKMailingAddress');
    if (isOptingIn) {
      setValue('principalPlaceOfBusiness', null);
      setValue('hasOptedForUKMailingAddress', true);
      setValue('registrationLocation', null);
      setValue('mailingAddress', undefined);
      setValue('operatingAddress', undefined);
    } else {
      setValue('registrationLocation', UKLocationOptions.EnglandAndWales);
      setValue('hasOptedForUKMailingAddress', false);
      setValue('mailingAddress.country', Country.GB);
    }
  };

  const isConvertingToLimitedCompany = (isConverting: boolean): void => {
    clearErrors('isConvertingToLimitedCompany');
    if (isConverting) {
      setValue('isConvertingToLimitedCompany', true);
      setValue('convertingToLimitedCompanyName', undefined);
      clearErrors('convertingToLimitedCompanyName');
    } else {
      setValue('isConvertingToLimitedCompany', false);
      setValue('convertingToLimitedCompanyName', '');
      clearErrors('convertingToLimitedCompanyName');
    }
  };

  const startSellDate = (value: CompanyBuySellStatus) => {
    clearErrors('hasNoSuchStartSellDate');
    switch (value) {
      case CompanyBuySellStatus.SameDate:
        clearErrors('hasDifferentDateInTheFuture');
        clearErrors('companyStartBuySellDate');
        setValue('hasDifferentDateInTheFuture', false);
        setDateSelection(CompanyBuySellStatus.SameDate);
        setValue('companyStartBuySellDate', null);
        break;

      case CompanyBuySellStatus.DifferentDate:
        setValue('hasDifferentDateInTheFuture', true);
        setDateSelection(CompanyBuySellStatus.DifferentDate);
        setValue(
          'companyStartBuySellDate',
          watch('companyStartBuySellDate') ?? null
        );
        break;

      case CompanyBuySellStatus.NoSuchDate:
        clearErrors('companyStartBuySellDate');
        setValue('companyStartBuySellDate', null);
        setValue('hasDifferentDateInTheFuture', false);
        setDateSelection(CompanyBuySellStatus.NoSuchDate);
        break;

      default:
        break;
    }
  };

  return (
    <form
      className="flex flex-col gap-10 max-w-[700px]"
      onSubmit={handleSubmit(onSubmit, (formError: unknown) => {
        console.error(formError);
      })}
    >
      <IncorporationFormHeading text="Company Information" />

      <FormInputWrapper
        id="companyRegisteredEmailAddress"
        text="Corporation name"
        description=""
        isRequired
      >
        <div>
          <CompanyNameInput
            label="Option 1"
            onValueChange={(val) => setValue('companyLegalName', val)}
            error={errors?.companyLegalName?.message}
            nameValidationOptions={{ country: Country.GB }}
            value={watch('companyLegalName')}
            placeholder="Entity name 1"
          />
        </div>

        <div>
          <CompanyNameInput
            label="Option 2"
            onValueChange={(val) => setValue('companyLegalName2', val)}
            error={errors?.companyLegalName2?.message}
            nameValidationOptions={{ country: Country.GB }}
            value={watch('companyLegalName2')}
            placeholder="Entity name 2"
          />
        </div>

        <div>
          <CompanyNameInput
            label="Option 3"
            onValueChange={(val) => setValue('companyLegalName3', val)}
            error={errors?.companyLegalName3?.message}
            nameValidationOptions={{ country: Country.GB }}
            value={watch('companyLegalName3')}
            placeholder="Entity name 3"
          />
        </div>
      </FormInputWrapper>

      <FormInputWrapper
        id="companyRegisteredEmailAddress"
        text="Company registered email address"
        isRequired
      >
        <Input
          type="email"
          placeholder="olivia@untitledui.com"
          id="companyRegisteredEmailAddress"
          {...register('companyRegisteredEmailAddress')}
          error={errors.companyRegisteredEmailAddress?.message}
          className="h-11 rounded-md border-border-primary p-2 gap-1.5 bg-bg-primary"
        />
      </FormInputWrapper>

      <FormInputWrapper
        id="hasOwnUKMailingAddress"
        text="Company's Registered Office Address"
        isRequired
      >
        <div className="flex flex-col gap-2">
          <RadioGroup
            defaultValue={
              watch('hasOptedForUKMailingAddress') === null
                ? undefined
                : watch('hasOptedForUKMailingAddress')
                  ? UKMailingAddressOptions.OptIn
                  : UKMailingAddressOptions.OwnAddress
            }
            className="flex flex-col gap-5"
            onValueChange={(value) => {
              clearErrors('mailingAddress');
              clearErrors('operatingAddress');
              if (value === UKMailingAddressOptions.OptIn)
                isOptingInUKMailingAddress(true);
              else isOptingInUKMailingAddress(false);
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={UKMailingAddressOptions.OptIn}
                id="opt-in"
              />
              <label htmlFor="opt-in" className="text-sm font-medium">
                Opt into Commenda&apos;s Registered Address service
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={UKMailingAddressOptions.OwnAddress}
                id="own-address"
              />
              <label htmlFor="own-address" className="text-sm font-medium">
                I have an address in the UK
              </label>
            </div>
          </RadioGroup>
        </div>
        {errors?.hasOptedForUKMailingAddress?.message && (
          <InputErrorMessage
            message={errors?.hasOptedForUKMailingAddress?.message}
          />
        )}
      </FormInputWrapper>

      {watch('hasOptedForUKMailingAddress') === false && (
        <>
          <FormInputWrapper
            id="registrationLocation"
            text="Where will the company be registered?"
            isRequired
          >
            <span className="body-15-regular text-neutral-2 leading-[22px]">
              You can&apos;t change where your company is registered after
              setup, except if your office is in Wales. In that case, you can
              choose &apos;England and Wales&apos; or &apos;Wales&apos; and
              change it later.
            </span>

            <div className="grid grid-cols-2 gap-3.5 mt-2">
              <Select
                value={watch('registrationLocation')}
                onValueChange={(val) => setValue('registrationLocation', val)}
              >
                <SelectTrigger error={errors?.registrationLocation?.message}>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(UKLocationOptions).map((option) => {
                    return (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {errors?.registrationLocation?.message && (
              <p className="text-red-600">
                {errors?.registrationLocation?.message}
              </p>
            )}
          </FormInputWrapper>

          <FormInputWrapper
            text="Physical address (communication address)"
            id="mailingAddress"
            isRequired
          >
            <AddressInput
              id="mailingAddress"
              address={watch('mailingAddress') as IAddress}
              disabled={{ country: true }}
              defaultCountry={Country.GB}
              onChange={(ad: IAddress) => {
                const update = {
                  ...ad,
                  country: Country.GB,
                  addressLine2: ad.addressLine2 ?? null,
                };
                setValue('mailingAddress', update);
              }}
              error={{
                addressLine1:
                  !watch('mailingAddress.addressLine1') &&
                  errors?.mailingAddress?.addressLine1?.message
                    ? errors?.mailingAddress?.addressLine1?.message
                    : null,
                city:
                  !watch('mailingAddress.city') &&
                  errors?.mailingAddress?.city?.message
                    ? errors?.mailingAddress?.city?.message
                    : null,
                postalCode:
                  !watch('mailingAddress.postalCode') &&
                  errors?.mailingAddress?.postalCode?.message
                    ? errors?.mailingAddress?.postalCode?.message
                    : null,
                state:
                  !watch('mailingAddress.state') &&
                  errors?.mailingAddress?.state?.message
                    ? errors?.mailingAddress?.state?.message
                    : undefined,
              }}
            />
          </FormInputWrapper>

          <FormInputWrapper
            id="principalPlaceOfBusiness"
            text="Where is the company's principal place of business?"
            description="This is where the company will carry out most of its business activities. If this will be in different locations, use the address where the company will keep its financial or business records. This does not reflect in public records."
            isRequired
          >
            <div className="flex flex-col gap-2">
              <RadioGroup
                defaultValue={watch('principalPlaceOfBusiness') || undefined}
                className="flex flex-col gap-5"
                onValueChange={(value) => {
                  clearErrors('principalPlaceOfBusiness');
                  setValue('principalPlaceOfBusiness', value);
                  if (value === PrincipalPlaceOfBusinessOptions.NoAddress) {
                    setValue('operatingAddress', undefined);
                  } else if (
                    value === PrincipalPlaceOfBusinessOptions.SameAddress
                  ) {
                    setValue('operatingAddress', undefined);
                  } else {
                    setValue('operatingAddress', defaultAddressSchema);
                  }
                }}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    size="lg"
                    value={PrincipalPlaceOfBusinessOptions.NoAddress}
                    id="no-address"
                  />
                  <label htmlFor="no-address" className="text-sm font-medium">
                    My company does not have a principal place of business yet
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    size="lg"
                    value={PrincipalPlaceOfBusinessOptions.SameAddress}
                    id="same-address"
                  />
                  <label htmlFor="same-address" className="text-sm font-medium">
                    Same as the registered address
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    size="lg"
                    value={PrincipalPlaceOfBusinessOptions.DifferentAddress}
                    id="different-address"
                  />
                  <label
                    htmlFor="different-address"
                    className="text-sm font-medium"
                  >
                    A different address from the registered address
                  </label>
                </div>
              </RadioGroup>
            </div>
            {errors?.principalPlaceOfBusiness?.message && (
              <InputErrorMessage
                message={errors?.principalPlaceOfBusiness?.message}
              />
            )}
          </FormInputWrapper>

          {watch('principalPlaceOfBusiness') ===
            PrincipalPlaceOfBusinessOptions.DifferentAddress && (
            <FormInputWrapper
              text="Operating address (principal address)"
              id="operatingAddress"
              isRequired
            >
              <AddressInput
                id="operatingAddress"
                address={watch('operatingAddress') as IAddress}
                onChange={(ad: IAddress) => {
                  const update = {
                    ...ad,
                    country: ad.country,
                    addressLine2: ad.addressLine2 ?? null,
                  };
                  setValue('operatingAddress', update);
                }}
                error={{
                  country:
                    errors?.operatingAddress?.country?.message &&
                    !watch('operatingAddress.country')
                      ? errors?.operatingAddress?.country?.message
                      : undefined,
                  addressLine1:
                    !watch('operatingAddress.addressLine1') &&
                    errors?.operatingAddress?.addressLine1?.message
                      ? errors?.operatingAddress?.addressLine1?.message
                      : null,
                  city:
                    !watch('operatingAddress.city') &&
                    errors?.operatingAddress?.city?.message
                      ? errors?.operatingAddress?.city?.message
                      : null,
                  postalCode:
                    !watch('operatingAddress.postalCode') &&
                    errors?.operatingAddress?.postalCode?.message
                      ? errors?.operatingAddress?.postalCode?.message
                      : null,
                  state:
                    !watch('operatingAddress.state') &&
                    errors?.operatingAddress?.state?.message
                      ? errors?.operatingAddress?.state?.message
                      : undefined,
                }}
              />
            </FormInputWrapper>
          )}
          {errors.hasOptedForUKMailingAddress?.message && (
            <InputErrorMessage
              message={errors.hasOptedForUKMailingAddress.message}
            />
          )}
        </>
      )}

      <FormInputWrapper
        id="isBuyingAnotherCompany"
        text="Is this new company replacing another business?"
        description="This includes if it's buying another company, or changing from another entity type to a limited company."
        isRequired
      >
        <div className="flex flex-col gap-2">
          <RadioGroup
            defaultValue={
              !watch('isConvertingToLimitedCompany')
                ? undefined
                : watch('isConvertingToLimitedCompany')
                  ? YesNoOptions.Yes
                  : YesNoOptions.No
            }
            className="flex flex-col gap-5"
            onValueChange={(value) => {
              if (value === YesNoOptions.Yes) {
                isConvertingToLimitedCompany(true);
              } else {
                isConvertingToLimitedCompany(false);
              }
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={YesNoOptions.Yes}
                id="converting-yes"
              />
              <label htmlFor="converting-yes" className="text-sm font-medium">
                Yes
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={YesNoOptions.No}
                id="converting-no"
              />
              <label htmlFor="converting-no" className="text-sm font-medium">
                No
              </label>
            </div>
          </RadioGroup>
        </div>
        {errors?.isConvertingToLimitedCompany?.message && (
          <InputErrorMessage
            message={errors?.isConvertingToLimitedCompany?.message}
          />
        )}
      </FormInputWrapper>

      {watch('isConvertingToLimitedCompany') === true && (
        <FormInputWrapper
          id="nameOfCompanyToBuy"
          text="What is the name of the business that you are acquiring?"
          isRequired
        >
          <Input
            placeholder="Enter name of business"
            id="nameOfCompanyToBuy"
            value={watch('convertingToLimitedCompanyName')}
            onChange={(e) => {
              clearErrors('convertingToLimitedCompanyName');
              setValue('convertingToLimitedCompanyName', e.target.value);
            }}
            error={errors?.convertingToLimitedCompanyName?.message}
          />
        </FormInputWrapper>
      )}

      <FormInputWrapper
        id="companyStartBuySellDate"
        text="When is the company expected to start buying and selling goods/services?"
        isRequired
      >
        <div className="flex flex-col gap-2">
          <RadioGroup
            defaultValue={
              dateSelection === CompanyBuySellStatus.SameDate
                ? CompanyBuySellStatus.SameDate
                : dateSelection === CompanyBuySellStatus.DifferentDate
                  ? CompanyBuySellStatus.DifferentDate
                  : dateSelection === CompanyBuySellStatus.NoSuchDate
                    ? CompanyBuySellStatus.NoSuchDate
                    : undefined
            }
            className="flex flex-col gap-5"
            onValueChange={(value: CompanyBuySellStatus) =>
              startSellDate(value)
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={CompanyBuySellStatus.SameDate}
                id="same-date"
              />
              <label htmlFor="same-date" className="text-sm font-medium">
                Same date as the set up
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={CompanyBuySellStatus.DifferentDate}
                id="different-date"
              />
              <label htmlFor="different-date" className="text-sm font-medium">
                Different date in the future
              </label>
            </div>

            {dateSelection === CompanyBuySellStatus.DifferentDate && (
              <FormInputWrapper
                id="companyStartBuySellDate"
                text="Choose a date"
              >
                <DatePicker
                  date={watch('companyStartBuySellDate')}
                  setDate={(dt) => {
                    clearErrors('companyStartBuySellDate');
                    setValue('companyStartBuySellDate', dt);
                  }}
                  _triggerClassName="min-w-fit w-full"
                  hasError={!!errors?.companyStartBuySellDate?.message}
                />
                {errors?.companyStartBuySellDate?.message && (
                  <InputErrorMessage
                    message={errors.companyStartBuySellDate.message}
                  />
                )}
              </FormInputWrapper>
            )}

            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={CompanyBuySellStatus.NoSuchDate}
                id="no-date"
              />
              <div className="flex flex-col">
                <label htmlFor="no-date" className="text-sm font-medium">
                  No such date
                </label>
                {dateSelection === CompanyBuySellStatus.NoSuchDate && (
                  <span className="text-sm text-gray-500">
                    You will have to communicate to Commenda when you start, so
                    we can convey this to the UK HMRC
                  </span>
                )}
              </div>
            </div>
          </RadioGroup>
        </div>
        {errors?.hasNoSuchStartSellDate?.message && (
          <InputErrorMessage
            message={errors?.hasNoSuchStartSellDate?.message}
          />
        )}
      </FormInputWrapper>

      <FieldWrapper>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col">
            <label className="text-base text-neutral-1 relative flex flex-col gap-2 items-start">
              <div className="flex flex-col">
                <span className="pb-2 font-bold">
                  In its first 3 months, will the company do any of the
                  following? <Required />
                </span>

                <ol className="list-decimal pl-[18px]">
                  <li>
                    <span>
                      Pay interest on any non-bank loans, for example to a
                      director who has loaned the company money
                    </span>
                  </li>
                  <li>
                    <span>
                      Make royalty payments, for example for using someone
                      else&apos;s patent
                    </span>
                  </li>
                  <li>
                    <span>
                      Receive interest or dividend payments from overseas
                      investments
                    </span>
                  </li>
                </ol>
                <span className="text-sm text-text-tertiary mb-3 mt-2">
                  These payments will have to be reported to the HM Revenue and
                  Customs using form CT61, and will be accounted for as Income
                  Tax.
                </span>
              </div>
            </label>
          </div>
          <RadioGroup
            defaultValue={
              watch('hasCT16Payments') === null
                ? undefined
                : watch('hasCT16Payments')
                  ? YesNoOptions.Yes
                  : YesNoOptions.No
            }
            className="flex flex-col gap-5"
            onValueChange={(value) => {
              clearErrors('hasCT16Payments');
              setValue('hasCT16Payments', value === YesNoOptions.Yes);
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                size="lg"
                value={YesNoOptions.Yes}
                id="ct16-yes"
              />
              <label htmlFor="ct16-yes" className="text-sm font-medium">
                Yes
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem size="lg" value={YesNoOptions.No} id="ct16-no" />
              <label htmlFor="ct16-no" className="text-sm font-medium">
                No
              </label>
            </div>
          </RadioGroup>
        </div>
        {errors?.hasCT16Payments?.message && (
          <InputErrorMessage message={errors?.hasCT16Payments?.message} />
        )}
      </FieldWrapper>

      <FormInputWrapper
        id="businessDescription"
        text="Business Description"
        description="In a few short sentences, please describe your primary business activities. Please also mention if you are moving goods in or out of the EU."
        isRequired
      >
        <Textarea
          {...register('businessDescription')}
          value={watch('businessDescription')}
          error={errors.businessDescription?.message}
          placeholder="Write a description"
        />
      </FormInputWrapper>

      <Button type="submit" disabled={isSubmitting} className="w-fit">
        <span>Continue</span>
        <ProceedIcon className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
};

export default CompanyInfo;
